import type { TypesenseClientHandle } from '~/composables/search/useSearchClient'
import { typesenseSearch } from '~/utils/typesenseClient'
import {
  SIMILAR_EVENTS_SOURCE_FIELDS,
  type TypesenseSimilarEventsSourceDoc,
} from '~/utils/typesenseSimilarEventsQuery'

/**
 * In-flight promise memo, keyed by collection + permalink. The similar events
 * and similar online events sections both need the same source doc on one
 * event page; without this each fires its own identical lookup.
 *
 * Module scope is safe ONLY on the client (same reasoning as useUserGeo) —
 * on the server it would be shared across requests, so it is bypassed there.
 */
const sourceEventPromises = new Map<
  string,
  Promise<TypesenseSimilarEventsSourceDoc | null>
>()

const requestTypesenseSourceEvent = async (
  permalink: string,
  collection: string,
  handle: TypesenseClientHandle
): Promise<TypesenseSimilarEventsSourceDoc | null> => {
  const result = await typesenseSearch<TypesenseSimilarEventsSourceDoc>(
    {
      collection,
      q: '*',
      query_by: 'title',
      filter_by: `permalink:=${permalink}`,
      include_fields: SIMILAR_EVENTS_SOURCE_FIELDS,
      per_page: 1,
    },
    // Same params for every viewer of this event — cache it.
    { ...handle, useCache: true }
  )
  return result.hits?.[0]?.document ?? null
}

/** Looks up an event's own Typesense document by permalink — its id is what the vector queries build from. */
export const fetchTypesenseSourceEvent = (
  permalink: string,
  collection: string,
  handle: TypesenseClientHandle
): Promise<TypesenseSimilarEventsSourceDoc | null> => {
  if (!import.meta.client) {
    return requestTypesenseSourceEvent(permalink, collection, handle)
  }

  const key = `${collection}:${permalink}`
  let promise = sourceEventPromises.get(key)
  if (!promise) {
    promise = requestTypesenseSourceEvent(permalink, collection, handle)
    // Drop failures so a later call can retry instead of replaying the error.
    promise.catch(() => sourceEventPromises.delete(key))
    sourceEventPromises.set(key, promise)
  }
  return promise
}
